/**
 * @file Component to present the forecast
 * @author Heino Lenting
 */
import * as tslib_1 from "tslib";
import { AfterContentChecked, OnDestroy, ChangeDetectorRef, AfterViewInit, NgZone, ElementRef } from '@angular/core';
import { PluginViewBase } from '../plugin-view/plugin-view.base';
import { DataSet } from '../../../core/helpers/data-set';
import { KeyedCollection } from '../../../base/helpers/remove-keyed-collection';
import { Processor } from '../../processors/processor';
import { AlertsService } from '../../../core/alerts.service';
import { LocalisationService } from '../../../core/localisation.service';
import { EventsService } from '../../../core/event.service';
import { DomainService } from '../../../domain.service';
import { ExecutorsService } from '../../executors.service';
import { SettingsService } from '../../../settings.service';
import { GridService } from '../../grid.service';
/**
 * Class to show the forecast, the unplanned services only.
 */
var PluginViewShiftsComponent = /** @class */ (function (_super) {
    tslib_1.__extends(PluginViewShiftsComponent, _super);
    /**
     * Creates an instance of PluginForecastShiftsComponent.
     * @param {LocalisationService} localisationService LocalisationService to get langauge dependent formats.
     * @param {ChangeDetectorRef} changeDetector ChangeDetectorRef to update the component on change
     * @param {SettingsService} settingsService SettingsService has settings we need.
     * @param {ExecutorsService} executorsService ExecutorsService needed for drag/drop actions.
     * @param {DomainService} domainService DomainService to get the shifts and services.
     */
    function PluginViewShiftsComponent(alertsService, localisationService, changeDetector, eventsService, settingsService, executorsService, domainService, elementRef, ngZone, gridService) {
        var _this = _super.call(this, localisationService, changeDetector, eventsService, gridService, settingsService) || this;
        _this.alertsService = alertsService;
        _this.executorsService = executorsService;
        _this.domainService = domainService;
        _this.elementRef = elementRef;
        _this.ngZone = ngZone;
        _this.gridService = gridService;
        /**
         * Reference to the selected team. The team is used to filter the shifts.
         * @private
         * @type {Entity}
         */
        _this._selectedTeam = null;
        /**
         * Reference to the selected function (or regio or team). The filter entity is used to filter the shifts.
         * @private
         * @type {Entity}
         */
        _this._filter = null;
        /**
         * The number of data refresh cycles are currently processing.
         * @protected
         * @memberOf PluginViewShiftsComponent
         */
        _this.loadingProcesses = 0;
        /**
         * Collection with query parameters that the user can change in value.
         * @protected
         * @type {DynamicParameter[]}
         */
        _this.dynamicParameters = [];
        /**
         * Collection with datasets per shift with the OID as key
         * @private
         */
        _this._datasets = new KeyedCollection();
        /**
         * Gets the executor name. Is used to indicate the source of a drag event.
         * @type {ExecutorName}
         */
        _this.executorName = 'drop';
        /**
        * Holds the reference of the component format.
        * @private
        * @type {ComponentConfiguration}
        */
        _this._format = undefined;
        return _this;
    }
    Object.defineProperty(PluginViewShiftsComponent.prototype, "loadingInProgress", {
        /**
         * Boolean to indicate loading in progress.
         * @public
         * @type {boolean}
         */
        get: function () {
            return this.loadingProcesses > 0;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(PluginViewShiftsComponent.prototype, "startDate", {
        /**
         * Gets he start date used for building the query
         * @readonly
         * @type {string}
         */
        get: function () {
            return this.gridService.level.formatter.queryStartDate.format('YYYY-MM-DD HH:mm:ss');
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(PluginViewShiftsComponent.prototype, "endDate", {
        /**
         * Gets, the end date used for building the query
         * @readonly
         * @type {string}
         */
        get: function () {
            return this.gridService.level.formatter.queryFinishDate.format('YYYY-MM-DD HH:mm:ss');
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(PluginViewShiftsComponent.prototype, "selectedFunction", {
        /**
         * Sets the options of the plugin. In this case the function to filter the services.
         * @type {Entity}
         * @memberOf PluginShiftsComponent
         */
        set: function (entity) {
            if (this._selectedFunction === entity) {
                return;
            }
            this._selectedFunction = entity;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(PluginViewShiftsComponent.prototype, "selectedTeam", {
        /**
         * Gets the selected team. The team is used to filter the shifts.
         * @readonly
         * @type {Entity}
         */
        get: function () {
            return this._selectedTeam;
        },
        /**
         * Sets the selected team. The team is used to filter the shifts.
         */
        set: function (value) {
            if (this._selectedTeam === value) {
                return;
            }
            this._selectedTeam = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(PluginViewShiftsComponent.prototype, "teamOid", {
        /**
         * Gets the query parameter value of the selected team.
         * @readonly
         * @type {string}
         */
        get: function () {
            if (this._selectedTeam == null) {
                return '?';
            }
            return this._selectedTeam['Oid'];
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(PluginViewShiftsComponent.prototype, "filterOid", {
        /**
         * Gets the query parameter value of the selected filter entity.
         * @readonly
         * @type {string}
         */
        get: function () {
            if (this._filter == null) {
                return '?';
            }
            return this._filter['Oid'] ? this._filter['Oid'] : '?';
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(PluginViewShiftsComponent.prototype, "filter", {
        /**
         * Gets the selected function (or regio or team). The filter entity is used to filter the shifts.
         * @readonly
         * @type {Entity}
         */
        get: function () {
            return this._filter;
        },
        /**
         * Sets the selected function (or regio or team). The filter entity is used to filter the shifts.
         */
        set: function (value) {
            if (value === this._filter) {
                return;
            }
            this._filter = value;
            this.refresh();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(PluginViewShiftsComponent.prototype, "pluginKey", {
        /**
         * Gets the plugin key. The key to store and find the timeslot collections of this plugin.
         * @readonly
         * @public
         * @type {string}
         * @memberOf PluginViewBase
         */
        get: function () {
            return Processor.pluginKey + '-view-shifts';
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(PluginViewShiftsComponent.prototype, "format", {
        /**
         * Get the formatof the lookup.
         */
        get: function () {
            return this._format;
        },
        /**
         * Sets the formatof the lookup
         */
        set: function (value) {
            if (value === this._format) {
                return;
            }
            this._format = value;
        },
        enumerable: true,
        configurable: true
    });
    PluginViewShiftsComponent.prototype.registerShift = function (dataSource) {
        var _this = this;
        this.shifts = new DataSet(this.domainService);
        this.shifts.sourceName = dataSource;
        this.shifts.queryOptions = {
            parameters: {
                filter: {
                    valueProperty: 'filterOid'
                },
                team: {
                    valueProperty: 'teamOid'
                },
                period: true,
                start: {
                    valueProperty: 'startDate'
                },
                finish: {
                    valueProperty: 'endDate'
                }
            }
        };
        DomainService.getDynamicParams(this.shifts.queryOptions, this.dynamicParameters);
        this.subscriptionPool.add(this.eventsService.register('drop-successful').subscribe(function () {
            _this.refresh();
        }));
        this.subscriptionPool.add(this.eventsService.register('plugin-selector-changed').subscribe(function () {
            _this.refresh();
        }));
        this.shifts.register('Shift');
    };
    /**
     * Callback when pagination is destroyed to cleanup references.
     */
    PluginViewShiftsComponent.prototype.ngOnDestroy = function () {
        this.subscriptionPool.unsubscribeAll();
    };
    /**
     * Handling the drag start event
     * @param {DragEvent} event The drag event
     * @param {string} executerName The name of the executor
     * @param {ExecutorData} data The data of the executor
     */
    PluginViewShiftsComponent.prototype.dragItem = function (dragEvent, executerName, data) {
        if (dragEvent.target instanceof Element) {
            // Create executor event and get the id returned
            var executorId = this.executorsService.createExecutorEventAndGetTheId(executerName, data);
            // Set the executor id
            /* Fix issue: Xerius: Bij aanlog via IE is het niet mogelijk om Afwezigheden /Services te droppen op Planbord #2718
             * Oplossing gebruik alleen 'Text' bij dataTransfer.
             * Zie: https://stackoverflow.com/questions/26213011/html5-dragdrop-issue-in-internet-explorer-datatransfer-property-access-not-pos
             */
            dragEvent.dataTransfer.setData('Text', executorId);
        }
    };
    /**
     * Gets the shifts and services to show.
     * @protected
     * @returns {Promise<QueryResult>} The result of the query
     */
    PluginViewShiftsComponent.prototype.refresh = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var result;
            var _this = this;
            return tslib_1.__generator(this, function (_a) {
                if (!this.shifts || this.shifts.entityType == null) {
                    return [2 /*return*/];
                }
                // generateGridSegments is hier nodig want daar wordt
                // gridService.level.formatter.queryStartDate en gridService.level.formatter.queryLastDate
                // gezet en die zijn nodig om de query request te doen met de juiste periode.
                this.gridCollections = this.generateGridSegments();
                result = new Promise(function (resolve, reject) { return tslib_1.__awaiter(_this, void 0, void 0, function () {
                    var harvestAll, formatter, count;
                    var _this = this;
                    return tslib_1.__generator(this, function (_a) {
                        switch (_a.label) {
                            case 0:
                                this.loadingProcesses++;
                                this.detectChanges();
                                DomainService.setDynamicParams(this.shifts.queryOptions, this.dynamicParameters, this);
                                return [4 /*yield*/, this.shifts.refresh()];
                            case 1:
                                _a.sent();
                                harvestAll = [];
                                // Stop loading even when there are no resources
                                if (this.shifts.items.length === 0) {
                                    formatter = this.gridService.level.formatter;
                                    formatter.refreshWith(this.pluginKey, harvestAll);
                                    resolve(null);
                                    if (this.loadingProcesses > 0) {
                                        this.loadingProcesses--;
                                    }
                                    this.detectChanges();
                                    return [2 /*return*/];
                                }
                                count = 0;
                                this.shifts.items.map(function (it) { return it; }).forEach(function (shift) { return tslib_1.__awaiter(_this, void 0, void 0, function () {
                                    var oid, dataset, queryOptions, queryResult, reason_1, formatter;
                                    return tslib_1.__generator(this, function (_a) {
                                        switch (_a.label) {
                                            case 0:
                                                oid = shift['Oid'];
                                                dataset = this._datasets.Item(oid);
                                                if (dataset === undefined) {
                                                    dataset = this._datasets.Add(oid, new DataSet());
                                                    dataset.sourceName = 'TaskList';
                                                }
                                                queryOptions = this.queryOptions(oid);
                                                dataset.queryOptions = queryOptions;
                                                _a.label = 1;
                                            case 1:
                                                _a.trys.push([1, 3, , 4]);
                                                return [4 /*yield*/, this.domainService.getDataSet(dataset, dataset.items)];
                                            case 2:
                                                queryResult = _a.sent();
                                                harvestAll = harvestAll.concat(dataset.items);
                                                resolve(queryResult);
                                                return [3 /*break*/, 4];
                                            case 3:
                                                reason_1 = _a.sent();
                                                this.loadingProcesses = 0;
                                                reject(reason_1);
                                                this.detectChanges();
                                                return [3 /*break*/, 4];
                                            case 4:
                                                count++;
                                                if (this.shifts.items.length === count) {
                                                    formatter = this.gridService.level.formatter;
                                                    formatter.refreshWith(this.pluginKey, harvestAll);
                                                    if (this.loadingProcesses > 0) {
                                                        this.loadingProcesses--;
                                                    }
                                                    this.detectChanges();
                                                }
                                                return [2 /*return*/];
                                        }
                                    });
                                }); });
                                return [2 /*return*/];
                        }
                    });
                }); });
                return [2 /*return*/, result];
            });
        });
    };
    /**
     * Create the QueryOptions for given shift
     * @param {string} shiftOid The oid of the shift
     * @returns {QueryOptions} The query options of the shift
     */
    PluginViewShiftsComponent.prototype.queryOptions = function (shiftOid) {
        return {
            parameters: {
                'className': 'Shift',
                'oid': shiftOid,
                'start': this.startDate,
                'finish': this.endDate,
                'include': this.getIncludeOfTasklist()
            },
            expand: 'Item.Shift'
        };
    };
    /**
     * Gets the include query parameter value.
     * @protected
     * @returns The include query parameter value.
     */
    PluginViewShiftsComponent.prototype.getIncludeOfTasklist = function () {
        if (!this._selectedFunction) {
            return 'Services UNPLANNED !PURPOSES[GENERATED]';
        }
        return 'Services UNPLANNED CONCERNS[' + this._selectedFunction['Oid'] + '] !PURPOSES[GENERATED]';
    };
    /**
     * Handling the drag over event
     * @param {DragEvent} event
     */
    PluginViewShiftsComponent.prototype.dragOver = function (event) {
        event.stopPropagation();
        event.preventDefault();
    };
    /**
     * Handling the drop event
     * @param {DragEvent} event
     */
    PluginViewShiftsComponent.prototype.dropItem = function (event) {
        var _this = this;
        // Prevent the default handling of the element
        event.preventDefault();
        // Get the executor id from the drag data
        /* Fix issue: Xerius: Bij aanlog via IE is het niet mogelijk om Afwezigheden /Services te droppen op Planbord #2718
         * Oplossing gebruik alleen 'Text' bij dataTransfer.
         * Zie: https://stackoverflow.com/questions/26213011/html5-dragdrop-issue-in-internet-explorer-datatransfer-property-access-not-pos
         */
        var executorId = event.dataTransfer.getData('Text');
        // Use the executor id to find the executor event
        var executorEvent = this.executorsService.getExecutorEventById(executorId);
        // By the drag start the executor name is set but if a drop is performed on the 'delete' button in
        // the DragSource we need to rewrite the orginal executor name to match against the correct delete executor.
        var executorName;
        switch (executorEvent.executorName) {
            case 'attendant-move':
                executorName = 'back';
                break;
        }
        if (executorName) {
            // Initiate the executors
            this.executorsService.initiate(executorName, executorEvent.data).then(function (_a) {
                var items = _a[0], successMessage = _a[1];
                // Execution is successful
                _this.alertsService.createAlert('success', successMessage);
                // Refresh the plugin itself
                _this.refresh();
            }).catch(function (error) {
                if (error.entityErrors && error.entityErrors.length > 0) {
                    // Error alert already created by executor
                    return;
                }
                _this.alertsService.createAlert('danger', (typeof error === 'string') ? error : 'APP.EXECUTORS.EXECUTION_FAILED', (typeof error === 'string') ? undefined : error);
            });
        }
        else {
            this.alertsService.createAlert('danger', 'APP.EXECUTORS.NO_DROP_BACK_SUPPORT');
        }
    };
    /**
     * Is called during the initialization of the component
     */
    PluginViewShiftsComponent.prototype.ngAfterViewInit = function () {
        var _this = this;
        // The dragover event is needed to make a drop work. The problem is that for every move the draggable
        // item fires the dragover event of Angular and a change detection is handled.  This is per pixel (!)
        // so it's quite slow to 're-render' the grid. Therefore I placed the dragover event outside Angular via NgZone.
        // This construction is used in grid-display-base.component.ts, grid-drag-base.component.ts, plugin-view-forecast-shifts and grid-bar-base.component.ts.
        this.ngZone.runOutsideAngular(function () {
            _this.elementRef.nativeElement.addEventListener('dragover', function (event) {
                event.stopPropagation();
                event.preventDefault();
            });
            _this.elementRef.nativeElement.addEventListener('drop', function (event) {
                event.stopPropagation();
                event.preventDefault();
            });
        });
        this.registerShift(this.format.dataSource);
        this.refresh();
    };
    return PluginViewShiftsComponent;
}(PluginViewBase));
export { PluginViewShiftsComponent };
